class CustomSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.dotPositions = [];
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    this.slider_container = this.querySelector(".u-grid");
    this.dotsContainer = this.querySelector("[data-slider-dots]");

    if (!this.slider_container) return;

    this.addEventListener("click", this.handleClick);
    this.addEventListener("keydown", this.handleKeydown);
    this.slider_container.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleResize);

    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("keydown", this.handleKeydown);
    this.slider_container?.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);

    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
    }
  }

  handleClick(event) {
    const backArrow = event.target.closest(".u-slider__arrow--back");
    const forwardArrow = event.target.closest(".u-slider__arrow--forward");
    const dot = event.target.closest("[data-slider-dot]");

    if (backArrow) {
      this.navigate("backward");
    } else if (forwardArrow) {
      this.navigate("forward");
    } else if (dot) {
      this.navigate(Number(dot.dataset.sliderDot));
    }
  }

  handleKeydown(event) {
    if (event.code === "ArrowLeft") {
      this.navigate("backward");
    } else if (event.code === "ArrowRight") {
      this.navigate("forward");
    }
  }

  handleResize() {
    this.update();
  }

  handleScroll() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateActiveDot();
    });
  }

  getSliderItems() {
    return [...this.slider_container.querySelectorAll(".u-slider__item")];
  }

  getDotPositions() {
    const items = this.getSliderItems();
    const maxScrollLeft = Math.max(
      0,
      this.slider_container.scrollWidth - this.slider_container.clientWidth
    );

    if (!items.length || maxScrollLeft <= 1) return [];

    const firstOffset = items[0].offsetLeft;
    const positions = [];

    items.forEach((item) => {
      const position = Math.min(Math.max(0, item.offsetLeft - firstOffset), maxScrollLeft);
      const lastPosition = positions[positions.length - 1];

      if (lastPosition === undefined || Math.abs(position - lastPosition) > 1) {
        positions.push(position);
      }
    });

    const lastPosition = positions[positions.length - 1];
    if (lastPosition !== undefined && Math.abs(maxScrollLeft - lastPosition) > 1) {
      positions.push(maxScrollLeft);
    }

    return positions;
  }

  isSliderActive() {
    const styles = window.getComputedStyle(this.slider_container);

    return (
      styles.display === "flex" &&
      this.slider_container.scrollWidth > this.slider_container.clientWidth + 1
    );
  }

  isDotsBreakpointEnabled() {
    if (!this.dotsContainer) return false;

    if (window.matchMedia("(max-width: 767px)").matches) {
      return this.classList.contains("sm-dots--visible");
    }

    if (window.matchMedia("(max-width: 1180px)").matches) {
      return this.classList.contains("md-dots--visible");
    }

    return this.classList.contains("lg-dots--visible");
  }

  update() {
    this.slider_item = this.querySelector(".u-slider__item");
    this.slider_item_width = this.slider_item?.offsetWidth || 0;
    this.dotPositions = this.getDotPositions();
    this.renderDots();
    this.updateActiveDot();
  }

  renderDots() {
    if (!this.dotsContainer) return;

    const shouldShowDots =
      this.isSliderActive() && this.isDotsBreakpointEnabled() && this.dotPositions.length > 1;

    this.dotsContainer.hidden = !shouldShowDots;

    if (!shouldShowDots) {
      this.dotsContainer.replaceChildren();
      return;
    }

    if (this.dotsContainer.children.length === this.dotPositions.length) return;

    const dotLabel = this.dotsContainer.dataset.sliderDotLabel || "Slide";
    const ofLabel = this.dotsContainer.dataset.sliderDotOf || "of";
    const fragment = document.createDocumentFragment();

    this.dotPositions.forEach((_, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "u-slider__dot";
      button.dataset.sliderDot = index;
      button.setAttribute(
        "aria-label",
        `${dotLabel} ${index + 1} ${ofLabel} ${this.dotPositions.length}`
      );
      fragment.appendChild(button);
    });

    this.dotsContainer.replaceChildren(fragment);
  }

  updateActiveDot() {
    if (!this.dotsContainer || this.dotsContainer.hidden || !this.dotPositions.length) return;

    const currentPosition = this.slider_container.scrollLeft;
    const activeIndex = this.dotPositions.reduce((closestIndex, position, index) => {
      const closestDistance = Math.abs(this.dotPositions[closestIndex] - currentPosition);
      const distance = Math.abs(position - currentPosition);

      return distance < closestDistance ? index : closestIndex;
    }, 0);

    this.dotsContainer.querySelectorAll("[data-slider-dot]").forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle("active", isActive);
      dot.setAttribute("aria-current", isActive ? "true" : "false");
    });
  }

  getNewScrollPosition = (arg) => {
    const gap = parseFloat(window.getComputedStyle(this.slider_container).columnGap) || 0;
    const scrollLeft = Math.round(this.slider_container.scrollLeft);
    const sliderWidth = Math.round(this.slider_container.clientWidth);
    const sliderScrollWidth = Math.round(this.slider_container.scrollWidth);
    const maxScrollLeft = sliderScrollWidth - sliderWidth;

    if (arg === "forward") {
      const x = scrollLeft + sliderWidth;
      return x > maxScrollLeft
        ? maxScrollLeft - scrollLeft < 5
          ? 0
          : maxScrollLeft
        : x;
    } else if (arg === "backward") {
      const x = scrollLeft - sliderWidth;
      return x < 0 ? (scrollLeft === 0 ? maxScrollLeft : 0) : x;
    } else if (typeof arg === "number") {
      return this.dotPositions[arg] ?? arg * (this.slider_item_width + gap);
    }
  };

  navigate = (arg) => {
    const position = this.getNewScrollPosition(arg);

    if (position === undefined) return;

    this.slider_container.scrollTo({
      left: position,
      behavior: "smooth",
    });
  };
}

if (!customElements.get("custom-slider-component")) {
  customElements.define("custom-slider-component", CustomSliderComponent);
}